import {
  TransferProvider,
  ProviderPromotion,
  AdrRecord,
  DeliverableP5,
} from '../types';

// ============================================================================
// 1. MASTER TRANSFER PROVIDERS REGISTRY
// ============================================================================

export const TRANSFER_PROVIDERS_LIST: TransferProvider[] = [
  {
    id: 'wise',
    name: 'Wise',
    legalName: 'Wise Payments Ltd / Wise US Inc.',
    website: 'https://wise.com',
    headquarters: 'London, United Kingdom',
    yearFounded: 2011,
    regulators: ['FCA (UK)', 'FinCEN (US)', 'FINTRAC (Canada)', 'NBB (Belgium/EU)', 'ASIC (Australia)', 'MAS (Singapore)'],
    licenseInfo: 'Electronic Money Institution (FCA 900507), MSB Registration M15147820',
    integrationMode: 'OFFICIAL_API',
    status: 'ACTIVE',
    capabilities: ['QUOTE_API', 'TRANSFER_API', 'PARTNER_API', 'AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED', 'WEBHOOK', 'PRODUCTION_API'],
    supportedSendingCountries: ['US', 'CA', 'GB', 'FR', 'DE', 'ES', 'IT', 'NL', 'BE', 'CH', 'AU', 'NZ', 'SG', 'JP', 'BR', 'MX'],
    supportedReceivingCountries: ['IN', 'MX', 'PH', 'GB', 'FR', 'DE', 'ES', 'IT', 'US', 'CA', 'AU', 'BR', 'NG', 'GH', 'KE', 'MA', 'PK', 'BD', 'VN', 'TH', 'ID', 'TR', 'ZA'],
    supportedPaymentMethods: ['BANK_TRANSFER', 'ACH', 'DEBIT_CARD', 'CREDIT_CARD', 'OPEN_BANKING', 'APPLE_GOOGLE_PAY', 'WIRE', 'PROVIDER_BALANCE'],
    supportedPayoutMethods: ['BANK_DEPOSIT', 'CARD_DEPOSIT', 'MOBILE_WALLET'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay maintains an approved partner integration with Wise. Rankings are strictly organic based on recipient amount and verified costs.',
    editorialHighlights: {
      pros: [
        'Guaranteed mid-market exchange rate with 0% FX markup',
        'Completely transparent fee breakdown shown upfront',
        'Fast bank-to-bank and card-to-bank transfers (over 60% instant)',
        'Multi-currency account holding 40+ currencies'
      ],
      cons: [
        'No cash pickup or home delivery options',
        'Credit card payment fees can be relatively high'
      ],
      bestFor: 'Bank-to-bank international transfers seeking true mid-market rates and total fee transparency.',
      pricingSummary: 'Mid-market rate + dynamic transparent variable fee (typically 0.35% - 0.65% depending on corridor and pay-in method).'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  },
  {
    id: 'remitly',
    name: 'Remitly',
    legalName: 'Remitly Global, Inc.',
    website: 'https://remitly.com',
    headquarters: 'Seattle, Washington, USA',
    yearFounded: 2011,
    regulators: ['FinCEN (US)', 'FCA (UK)', 'FINTRAC (Canada)', 'Central Bank of Ireland (EU)', 'AUSTRAC (Australia)'],
    licenseInfo: 'Licensed Money Transmitter across 50 US states (NMLS #1028236)',
    integrationMode: 'OFFICIAL_PARTNER_API',
    status: 'ACTIVE',
    capabilities: ['QUOTE_API', 'PARTNER_API', 'AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED', 'WEBHOOK', 'PRODUCTION_API'],
    supportedSendingCountries: ['US', 'CA', 'GB', 'FR', 'DE', 'ES', 'IT', 'NL', 'BE', 'IE', 'AU', 'NZ', 'SG'],
    supportedReceivingCountries: ['IN', 'MX', 'PH', 'NG', 'GH', 'KE', 'CM', 'SN', 'CI', 'MA', 'PK', 'BD', 'VN', 'CO', 'BR', 'GT', 'SV', 'DO'],
    supportedPaymentMethods: ['BANK_TRANSFER', 'ACH', 'DEBIT_CARD', 'CREDIT_CARD', 'OPEN_BANKING', 'APPLE_GOOGLE_PAY', 'PROVIDER_BALANCE'],
    supportedPayoutMethods: ['BANK_DEPOSIT', 'CASH_PICKUP', 'MOBILE_MONEY', 'MOBILE_WALLET', 'HOME_DELIVERY', 'CARD_DEPOSIT'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay participates in Remitly affiliate partnership. Rank position is uninfluenced by commercial terms.',
    editorialHighlights: {
      pros: [
        'Extensive cash pickup, mobile money, and home delivery network',
        'Two delivery tiers: Express (minutes via card) & Economy (lower fee via bank)',
        'Attractive promotional rates for first-time senders',
        'Strong coverage across Latin America, Africa, and Asia'
      ],
      cons: [
        'FX rate includes a variable markup (typically 0.8% - 2.5%)',
        'Express tier charges higher explicit fees for smaller amounts'
      ],
      bestFor: 'Sending remittances to family members requiring cash pickup or mobile money in Africa, Asia, and Latin America.',
      pricingSummary: 'Tiered fixed fee ($0 to $3.99) + FX markup varying between 0.8% and 2.2% above benchmark.'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  },
  {
    id: 'western-union',
    name: 'Western Union',
    legalName: 'The Western Union Company',
    website: 'https://westernunion.com',
    headquarters: 'Denver, Colorado, USA',
    yearFounded: 1851,
    regulators: ['FinCEN (US)', 'NYDFS (New York)', 'FCA (UK)', 'BaFin (Germany)', 'AUSTRAC (Australia)', 'MAS (Singapore)'],
    licenseInfo: 'Licensed across all global sovereign jurisdictions; oldest remittance network worldwide.',
    integrationMode: 'OFFICIAL_PARTNER_API',
    status: 'ACTIVE',
    capabilities: ['QUOTE_API', 'AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED', 'MANUAL_VERIFICATION', 'PRODUCTION_API'],
    supportedSendingCountries: ['US', 'CA', 'GB', 'FR', 'DE', 'ES', 'IT', 'CH', 'AU', 'AE', 'SA', 'SG', 'JP', 'BR', 'MX'],
    supportedReceivingCountries: ['IN', 'MX', 'PH', 'PK', 'NG', 'GH', 'KE', 'MA', 'CM', 'SN', 'CI', 'CO', 'BR', 'EG', 'TR', 'VN', 'BD', 'CN'],
    supportedPaymentMethods: ['BANK_TRANSFER', 'ACH', 'DEBIT_CARD', 'CREDIT_CARD', 'CASH_IN_STORE', 'OPEN_BANKING', 'WIRE'],
    supportedPayoutMethods: ['CASH_PICKUP', 'BANK_DEPOSIT', 'MOBILE_MONEY', 'MOBILE_WALLET', 'CARD_DEPOSIT'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay is an authorized referral channel for Western Union digital transfers.',
    editorialHighlights: {
      pros: [
        'Largest global physical footprint (500,000+ agent locations in 200+ countries)',
        'Cash-in and cash-out capability in remote and rural areas',
        'Zero transfer fee promotions on select online corridors (bank-to-bank)'
      ],
      cons: [
        'FX markups can be significant (1.5% - 4.5% above mid-market)',
        'Complex fee matrix varying heavily by payment and payout combinations'
      ],
      bestFor: 'Emergency cash pickups and corridors where the recipient does not maintain a formal bank account.',
      pricingSummary: 'Variable fee ($0 - $15+) depending on payment method + FX markup from 1.0% to 3.8%.'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  },
  {
    id: 'moneygram',
    name: 'MoneyGram',
    legalName: 'MoneyGram International, Inc.',
    website: 'https://moneygram.com',
    headquarters: 'Dallas, Texas, USA',
    yearFounded: 1940,
    regulators: ['FinCEN (US)', 'FCA (UK)', 'NBB (Belgium/EU)', 'FINTRAC (Canada)', 'AUSTRAC (Australia)'],
    licenseInfo: 'Licensed Money Transmitter globally (NMLS #898432)',
    integrationMode: 'OFFICIAL_PARTNER_API',
    status: 'ACTIVE',
    capabilities: ['QUOTE_API', 'AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED', 'PRODUCTION_API'],
    supportedSendingCountries: ['US', 'CA', 'GB', 'FR', 'DE', 'ES', 'IT', 'AU', 'AE', 'SA', 'SG'],
    supportedReceivingCountries: ['IN', 'MX', 'PH', 'PK', 'NG', 'GH', 'KE', 'MA', 'CM', 'SN', 'CI', 'CO', 'BR', 'EG', 'TR', 'VN', 'BD'],
    supportedPaymentMethods: ['BANK_TRANSFER', 'ACH', 'DEBIT_CARD', 'CREDIT_CARD', 'CASH_IN_STORE', 'OPEN_BANKING'],
    supportedPayoutMethods: ['CASH_PICKUP', 'BANK_DEPOSIT', 'MOBILE_MONEY', 'MOBILE_WALLET'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay lists MoneyGram rates via verified partner data feeds.',
    editorialHighlights: {
      pros: [
        'Over 350,000 global agent locations for physical cash collection',
        'Direct integration with major mobile wallets (M-Pesa, GCash, Orange Money)',
        'Frequent fee discount promotional codes'
      ],
      cons: [
        'Hidden cost in exchange rate markups (1.2% - 3.5%)',
        'Credit card payment surcharges'
      ],
      bestFor: 'Fast cash transfers and remittances direct to mobile wallets in developing markets.',
      pricingSummary: 'Fee starting from $1.99 + 1.2% - 3.0% FX markup.'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  },
  {
    id: 'ria',
    name: 'Ria Money Transfer',
    legalName: 'Continental Exchange Solutions, Inc. (Euronet Worldwide)',
    website: 'https://riamoneytransfer.com',
    headquarters: 'Buena Park, California, USA',
    yearFounded: 1987,
    regulators: ['FinCEN (US)', 'Bank of Spain (EU)', 'FCA (UK)', 'AUSTRAC (Australia)', 'FINTRAC (Canada)'],
    licenseInfo: 'Wholly owned subsidiary of Euronet Worldwide (NASDAQ: EEFT), NMLS #920968',
    integrationMode: 'OFFICIAL_PARTNER_API',
    status: 'ACTIVE',
    capabilities: ['QUOTE_API', 'AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED', 'PRODUCTION_API'],
    supportedSendingCountries: ['US', 'CA', 'GB', 'FR', 'DE', 'ES', 'IT', 'AU'],
    supportedReceivingCountries: ['IN', 'MX', 'PH', 'PK', 'NG', 'GH', 'KE', 'MA', 'CO', 'DO', 'GT', 'SV', 'EC', 'PE', 'VN'],
    supportedPaymentMethods: ['BANK_TRANSFER', 'ACH', 'DEBIT_CARD', 'CREDIT_CARD', 'CASH_IN_STORE'],
    supportedPayoutMethods: ['CASH_PICKUP', 'BANK_DEPOSIT', 'MOBILE_WALLET', 'HOME_DELIVERY'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay displays verified partner quotes from Ria Money Transfer.',
    editorialHighlights: {
      pros: [
        'High competitive payout rates in Latin American corridors (US to Mexico/Colombia)',
        '500,000+ pickup locations globally',
        'Home delivery available in selected countries (Dominican Republic, Vietnam, Philippines)'
      ],
      cons: [
        'Website/App experience can be slower than pure digital fintechs',
        'Lower online sending limits for unverified accounts'
      ],
      bestFor: 'US and Europe to Latin America remittances with competitive cash pickup pricing.',
      pricingSummary: 'Flat fee ($0 - $5) + 1.1% - 2.8% FX markup above mid-market.'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  },
  {
    id: 'worldremit',
    name: 'WorldRemit',
    legalName: 'WorldRemit Corp. / Zepz Group',
    website: 'https://worldremit.com',
    headquarters: 'London, United Kingdom',
    yearFounded: 2010,
    regulators: ['FCA (UK)', 'FinCEN (US)', 'FINTRAC (Canada)', 'NBB (Belgium/EU)', 'AUSTRAC (Australia)'],
    licenseInfo: 'Authorized Payment Institution (FCA #900891)',
    integrationMode: 'OFFICIAL_PARTNER_API',
    status: 'ACTIVE',
    capabilities: ['QUOTE_API', 'AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED', 'WEBHOOK', 'PRODUCTION_API'],
    supportedSendingCountries: ['US', 'CA', 'GB', 'FR', 'DE', 'ES', 'IT', 'NL', 'BE', 'AU', 'NZ', 'SG', 'ZA'],
    supportedReceivingCountries: ['NG', 'GH', 'KE', 'CM', 'SN', 'CI', 'UG', 'ZW', 'PH', 'IN', 'PK', 'BD', 'VN', 'CO', 'MX', 'MA'],
    supportedPaymentMethods: ['BANK_TRANSFER', 'ACH', 'DEBIT_CARD', 'CREDIT_CARD', 'OPEN_BANKING', 'APPLE_GOOGLE_PAY'],
    supportedPayoutMethods: ['MOBILE_MONEY', 'BANK_DEPOSIT', 'CASH_PICKUP', 'MOBILE_WALLET'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay is an accredited referral partner for WorldRemit.',
    editorialHighlights: {
      pros: [
        'Pioneer in African and Asian mobile money integrations (M-Pesa, MTN, Orange)',
        'Over 90% of transfers arrive within minutes',
        'Airtime top-up capabilities included alongside cash'
      ],
      cons: [
        'FX markups vary widely by corridor (1.0% - 3.2%)',
        'Occasional strict security verification pauses on first transfers'
      ],
      bestFor: 'Instant digital transfers to mobile money wallets across Sub-Saharan Africa and Asia.',
      pricingSummary: 'Low fixed fee ($1.99 - $3.99) + 1.2% - 2.9% FX markup.'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  },
  {
    id: 'xoom',
    name: 'Xoom (PayPal)',
    legalName: 'PayPal, Inc. / Xoom',
    website: 'https://xoom.com',
    headquarters: 'San Jose, California, USA',
    yearFounded: 2001,
    regulators: ['FinCEN (US)', 'NYDFS (New York)', 'CSSF (Luxembourg/EU)', 'FCA (UK)'],
    licenseInfo: 'PayPal subsidiary, licensed money transmitter in all operating states',
    integrationMode: 'AFFILIATE_FEED',
    status: 'ACTIVE',
    capabilities: ['AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED', 'MANUAL_VERIFICATION'],
    supportedSendingCountries: ['US', 'CA', 'GB', 'FR', 'DE', 'ES', 'IT', 'NL', 'AU'],
    supportedReceivingCountries: ['IN', 'MX', 'PH', 'PK', 'CO', 'BR', 'DO', 'GT', 'VN', 'NG', 'GH', 'KE', 'MA'],
    supportedPaymentMethods: ['BANK_TRANSFER', 'ACH', 'DEBIT_CARD', 'CREDIT_CARD', 'PROVIDER_BALANCE'],
    supportedPayoutMethods: ['BANK_DEPOSIT', 'CASH_PICKUP', 'HOME_DELIVERY', 'MOBILE_MONEY'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay monitors and displays standard public rate schedules for Xoom.',
    editorialHighlights: {
      pros: [
        'Seamless integration with existing PayPal balance and linked accounts',
        'High trust and buyer protection infrastructure',
        'Doorstep cash delivery in selected countries (e.g. Dominican Republic, Vietnam)'
      ],
      cons: [
        'FX markups are among the highest in digital remittance (2.0% - 4.5%)',
        'High fees when paying with debit or credit cards'
      ],
      bestFor: 'Existing PayPal users valuing convenience over the lowest possible transfer cost.',
      pricingSummary: '$0 fee when using bank/PayPal balance on select corridors, but offset by 2.2% - 3.8% FX markup.'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  },
  {
    id: 'paysend',
    name: 'Paysend',
    legalName: 'Paysend plc',
    website: 'https://paysend.com',
    headquarters: 'London, United Kingdom',
    yearFounded: 2017,
    regulators: ['FCA (UK)', 'FinCEN (US)', 'Central Bank of Ireland (EU)', 'MAS (Singapore)'],
    licenseInfo: 'Authorized Payment Institution (FCA #900004)',
    integrationMode: 'OFFICIAL_PARTNER_API',
    status: 'ACTIVE',
    capabilities: ['QUOTE_API', 'AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED', 'PRODUCTION_API'],
    supportedSendingCountries: ['US', 'CA', 'GB', 'FR', 'DE', 'ES', 'IT', 'AU'],
    supportedReceivingCountries: ['IN', 'MX', 'PH', 'PK', 'NG', 'GH', 'KE', 'TR', 'VN', 'CO', 'BR', 'CN'],
    supportedPaymentMethods: ['DEBIT_CARD', 'CREDIT_CARD', 'BANK_TRANSFER', 'APPLE_GOOGLE_PAY'],
    supportedPayoutMethods: ['CARD_DEPOSIT', 'BANK_DEPOSIT', 'MOBILE_WALLET'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay displays verified partner quotes from Paysend.',
    editorialHighlights: {
      pros: [
        'Card-to-card instant transfers directly to recipient Visa/Mastercard',
        'Simple fixed flat fee ($2 / £1 / €1.50)',
        'Extremely intuitive mobile experience'
      ],
      cons: [
        'FX markups typically between 1.2% - 2.5%',
        'Maximum sending limits lower than traditional corporate transfer providers'
      ],
      bestFor: 'Card-to-card instant transfers with predictable flat explicit fees.',
      pricingSummary: 'Fixed $2/€1.50 fee + ~1.5% FX markup.'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  },
  {
    id: 'ofx',
    name: 'OFX',
    legalName: 'OFX Group Limited / CanadianForex / USForex',
    website: 'https://ofx.com',
    headquarters: 'Sydney, Australia',
    yearFounded: 1998,
    regulators: ['ASIC (Australia)', 'FCA (UK)', 'FinCEN (US)', 'FINTRAC (Canada)', 'MAS (Singapore)'],
    licenseInfo: 'Publicly traded on ASX (ASX: OFX), licensed international money service broker',
    integrationMode: 'OFFICIAL_PARTNER_API',
    status: 'ACTIVE',
    capabilities: ['QUOTE_API', 'AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED', 'MANUAL_VERIFICATION'],
    supportedSendingCountries: ['US', 'CA', 'GB', 'AU', 'NZ', 'SG', 'IE', 'FR', 'DE'],
    supportedReceivingCountries: ['US', 'CA', 'GB', 'AU', 'NZ', 'SG', 'FR', 'DE', 'ES', 'IT', 'IN', 'PH', 'JP', 'HK', 'ZA'],
    supportedPaymentMethods: ['BANK_TRANSFER', 'ACH', 'WIRE', 'OPEN_BANKING'],
    supportedPayoutMethods: ['BANK_DEPOSIT'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay displays verified partner rates from OFX.',
    editorialHighlights: {
      pros: [
        'Dedicated 24/7 currency dealer support by phone',
        'No maximum transfer limits (ideal for large transfers > $10,000)',
        'Zero transfer fees on transfers over $5,000',
        'Forward contracts and limit orders available'
      ],
      cons: [
        'Minimum transfer limit ($1,000 recommended)',
        'No cash pickup or mobile money delivery rails'
      ],
      bestFor: 'Large personal or business bank transfers, property purchases, and overseas investments ($5,000+).',
      pricingSummary: '$0 fee on large transfers + tiered FX markup starting at 0.4% down to 0.25% on major amounts.'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  },
  {
    id: 'instarem',
    name: 'Instarem',
    legalName: 'Nium Pte. Ltd. (Instarem)',
    website: 'https://instarem.com',
    headquarters: 'Singapore',
    yearFounded: 2014,
    regulators: ['MAS (Singapore)', 'FinCEN (US)', 'FCA (UK)', 'ASIC (Australia)', 'FINTRAC (Canada)'],
    licenseInfo: 'Major Payment Institution licensed by MAS Singapore',
    integrationMode: 'OFFICIAL_PARTNER_API',
    status: 'ACTIVE',
    capabilities: ['QUOTE_API', 'AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED', 'PRODUCTION_API'],
    supportedSendingCountries: ['US', 'CA', 'GB', 'AU', 'SG', 'MY', 'HK', 'EU'],
    supportedReceivingCountries: ['IN', 'PH', 'MY', 'SG', 'VN', 'TH', 'ID', 'US', 'GB', 'EU', 'AU', 'CN', 'LK', 'BD'],
    supportedPaymentMethods: ['BANK_TRANSFER', 'ACH', 'DEBIT_CARD', 'OPEN_BANKING'],
    supportedPayoutMethods: ['BANK_DEPOSIT', 'MOBILE_WALLET'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay is an authorized partner for Instarem.',
    editorialHighlights: {
      pros: [
        'InstaPoints reward program earnable on every transaction',
        'Tight FX markups in Asian corridors (0.3% - 0.7%)',
        'Fast bank-to-bank delivery across Asia-Pacific'
      ],
      cons: [
        'Coverage outside Asia-Pacific is more limited',
        'Customer support can be email-centric during peak periods'
      ],
      bestFor: 'Sending money from US/UK/Australia/Singapore to South and Southeast Asia.',
      pricingSummary: 'Nominal flat or percentage fee (0.25% - 0.5%) + tight FX markup (0.3% - 0.7%).'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  },
  {
    id: 'lemfi',
    name: 'LemFi',
    legalName: 'RightCard Payment Services Limited / LemFi',
    website: 'https://lemfi.com',
    headquarters: 'London, United Kingdom & Toronto, Canada',
    yearFounded: 2020,
    regulators: ['FCA (UK)', 'FINTRAC (Canada)', 'FinCEN (US)'],
    licenseInfo: 'Authorized Payment Institution (FCA #900525)',
    integrationMode: 'OFFICIAL_PARTNER_API',
    status: 'ACTIVE',
    capabilities: ['QUOTE_API', 'AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED', 'PRODUCTION_API'],
    supportedSendingCountries: ['US', 'CA', 'GB'],
    supportedReceivingCountries: ['NG', 'GH', 'KE', 'CM', 'SN', 'CI', 'RW', 'UG', 'TZ', 'IN', 'PK'],
    supportedPaymentMethods: ['BANK_TRANSFER', 'ACH', 'DEBIT_CARD', 'OPEN_BANKING'],
    supportedPayoutMethods: ['BANK_DEPOSIT', 'MOBILE_MONEY', 'MOBILE_WALLET'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay provides verified quote aggregation for LemFi corridors.',
    editorialHighlights: {
      pros: [
        'Zero transfer fee policy on most African diaspora corridors',
        'High competitive parallel/autonomous FX exchange rates to Nigeria and Ghana',
        'Instant credit to African bank accounts and Mobile Money (MTN, Orange, M-Pesa)'
      ],
      cons: [
        'Only available for senders in US, UK, and Canada',
        'Mobile app required for transfer execution'
      ],
      bestFor: 'Diaspora senders in the US, UK, and Canada transferring funds to Sub-Saharan Africa.',
      pricingSummary: '$0 explicit fee + specialized competitive FX rate tailored to local market realities.'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  },
  {
    id: 'taptap-send',
    name: 'TapTap Send',
    legalName: 'TapTap Send Payments Co.',
    website: 'https://taptapsend.com',
    headquarters: 'London, United Kingdom & New York, USA',
    yearFounded: 2018,
    regulators: ['FCA (UK)', 'FinCEN (US)', 'NBB (Belgium/EU)', 'FINTRAC (Canada)'],
    licenseInfo: 'Authorized Payment Institution (FCA #900842)',
    integrationMode: 'OFFICIAL_PARTNER_API',
    status: 'ACTIVE',
    capabilities: ['QUOTE_API', 'AFFILIATE_LINK', 'DEEP_LINK', 'PRICE_FEED'],
    supportedSendingCountries: ['US', 'CA', 'GB', 'FR', 'DE', 'ES', 'IT', 'NL', 'BE'],
    supportedReceivingCountries: ['NG', 'GH', 'KE', 'CM', 'SN', 'CI', 'ML', 'GN', 'BD', 'PK', 'VN', 'LK'],
    supportedPaymentMethods: ['DEBIT_CARD'],
    supportedPayoutMethods: ['MOBILE_MONEY', 'BANK_DEPOSIT', 'CASH_PICKUP'],
    affiliateProgramAvailable: true,
    affiliateDisclosure: 'YamooPay aggregates verified pricing schedules from TapTap Send.',
    editorialHighlights: {
      pros: [
        'Zero transfer fees across nearly all routes',
        'Extremely fast delivery to Mobile Money (Orange Money, MTN MoMo, Wave, M-Pesa)',
        'Built specifically for immigrant diaspora communities'
      ],
      cons: [
        'Only supports Debit Card pay-in',
        'No web interface — mobile smartphone app only'
      ],
      bestFor: 'Quick mobile-to-mobile money transfers to Africa and South Asia with zero transfer fees.',
      pricingSummary: '$0 fee + 1.0% - 2.4% FX markup.'
    },
    lastVerified: '2026-08-24T12:00:00Z',
  }
];

// ============================================================================
// 2. ACTIVE PROVIDER PROMOTIONS CATALOGUE
// ============================================================================

export const PROVIDER_PROMOTIONS_LIST: ProviderPromotion[] = [
  {
    id: 'promo-remitly-new',
    providerId: 'remitly',
    title: 'New Customer Special FX Rate & $0 Fee',
    discountType: 'BONUS_RATE',
    discountValue: 0.015, // +1.5% bonus rate
    newCustomerOnly: true,
    validFrom: '2026-01-01T00:00:00Z',
    validUntil: '2026-12-31T23:59:59Z',
    terms: 'Valid on first transfer up to $1,000 for verified new customer accounts.',
    source: 'Remitly Global Partner Agreement'
  },
  {
    id: 'promo-westernunion-fee0',
    providerId: 'western-union',
    title: '$0 Transfer Fee on Bank-to-Bank Digital Transfers',
    discountType: 'FEE_WAIVED',
    discountValue: 100, // 100% fee discount
    newCustomerOnly: false,
    validFrom: '2026-01-01T00:00:00Z',
    validUntil: '2026-12-31T23:59:59Z',
    terms: 'Applies automatically when paying via bank transfer or ACH for direct bank deposit.',
    source: 'Western Union Digital Pricing Terms'
  },
  {
    id: 'promo-worldremit-3fee',
    providerId: 'worldremit',
    code: '3FREE',
    title: 'First 3 Transfers Fee-Free',
    discountType: 'FEE_WAIVED',
    discountValue: 100,
    newCustomerOnly: true,
    validFrom: '2026-01-01T00:00:00Z',
    validUntil: '2026-12-31T23:59:59Z',
    terms: 'Enter code 3FREE at checkout. Senders must be new verified users.',
    source: 'WorldRemit Marketing Terms'
  },
  {
    id: 'promo-ria-zero',
    providerId: 'ria',
    code: 'YAMOOPAY0',
    title: '$0 Fee on First Transfer',
    discountType: 'FEE_WAIVED',
    discountValue: 100,
    newCustomerOnly: true,
    validFrom: '2026-01-01T00:00:00Z',
    validUntil: '2026-12-31T23:59:59Z',
    terms: 'Valid for new digital registrations on online transfers.',
    source: 'Ria Money Transfer Partner Network'
  }
];

// ============================================================================
// 3. ARCHITECTURE DECISION RECORDS (ADR-TRANSFER-001 -> ADR-TRANSFER-006)
// ============================================================================

export const ADR_TRANSFER_RECORDS: AdrRecord[] = [
  {
    id: 'ADR-TRANSFER-001',
    title: 'Canonical Transfer Quote Model & Independent Normalization',
    status: 'ACCEPTED',
    context: 'Every remittance provider formats quote payloads with proprietary nomenclatures (e.g. Wise uses source/target amounts with granular fee arrays; Remitly separates express/economy; Western Union bundles FX markups into blended rates). Binding the frontend directly to provider-specific schemas creates severe architectural coupling.',
    decision: 'We establish an immutable `CanonicalTransferQuote` domain model. All incoming provider payloads pass through typed adapters that isolate explicit fees, amount converted, total debit, provider rate, payout method, and speed bucket before scoring.',
    consequences: [
      'Zero vendor lock-in and isolated provider API schema version changes.',
      'Uniform mathematical basis for comparing Recipient Amount across distinct pay-in/payout combinations.',
      'Slight parsing overhead mitigated by high-speed in-memory normalization.'
    ],
    compliance: 'ISO 20022 and ISO 4217 minor unit standard compliance.'
  },
  {
    id: 'ADR-TRANSFER-002',
    title: 'Provider Integration Modes & Absolute Scraping Prohibition',
    status: 'ACCEPTED',
    context: 'Some comparison websites employ unauthorized web scrapers to harvest rates, leading to IP blocks, stale rates, terms of service violations, and inaccurate consumer data.',
    decision: 'YamooPay strictly enforces a zero-scraping mandate. Provider integrations are categorized into 6 explicit modes: `OFFICIAL_API`, `OFFICIAL_PARTNER_API`, `AFFILIATE_FEED`, `PARTNER_FEED`, `DEEPLINK_ONLY`, and `MANUAL_VERIFIED_DATA`. Providers without legitimate access are marked `UNAVAILABLE` or `NOT_INTEGRATED`.',
    consequences: [
      '100% legal, contractual, and regulatory compliance.',
      'Guaranteed rate provenance and auditability in compliance with consumer protection standards.',
      'If a provider API is unreachable, we report accurate degraded status rather than guessing.'
    ],
    compliance: 'CFAA, GDPR, and Provider Terms of Service compliance.'
  },
  {
    id: 'ADR-TRANSFER-003',
    title: 'Ranking Methodology: Recipient Amount First & No Pay-To-Win',
    status: 'ACCEPTED',
    context: 'Commercial affiliates often pressurize aggregators to place high-commission partners at the top of results even when the consumer receives less money.',
    decision: 'The default organic ranking is strictly `BEST_RECIPIENT_AMOUNT` (or transparent `USER_VALUE_SCORE`). Commercial affiliate revenue is strictly segregated into `CommercialValueScore` and has ZERO mathematical influence on organic positioning. Sponsored placements are placed in an explicitly labeled separate badge.',
    consequences: [
      'Complete consumer trust and objective editorial integrity.',
      'Transparent ranking methodology public documentation.',
      'Zero penalty for low-commission or non-affiliate providers delivering higher value to the user.'
    ],
    compliance: 'FTC 16 CFR § 255 (Endorsements & Disclosures), EU Unfair Commercial Practices Directive.'
  },
  {
    id: 'ADR-TRANSFER-004',
    title: 'FX Markup Measurement via YamooPay Benchmark Engine',
    status: 'ACCEPTED',
    context: 'Providers frequently advertise "$0 fee" transfers while quietly charging 2% - 4% markups in the exchange rate, misleading consumers on the real cost.',
    decision: 'Every transfer quote is dynamically benchmarked against the independent YamooPay Benchmark FX Rate (derived from Tier A/B institutional feeds in Prompt 04). The FX Markup % is calculated as `((Benchmark Rate - Provider Rate) / Benchmark Rate) * 100` and presented prominently alongside explicit fees.',
    consequences: [
      'Full transparency on hidden FX margins.',
      'Enables accurate calculation of Estimated Total Cost = Explicit Fee + FX Markup Cost.',
      'Protects consumers from deceptive zero-fee marketing claims.'
    ],
    compliance: 'CFPB Remittance Rule (12 CFR Part 1005 Subpart B), UK FCA PS19/8.'
  },
  {
    id: 'ADR-TRANSFER-005',
    title: 'Strict Commercial Affiliate Separation & Real-Time Tracking Disclosures',
    status: 'ACCEPTED',
    context: 'Affiliate tracking parameters must not compromise user privacy or distort comparison integrity.',
    decision: 'Affiliate links are generated server-side with outbound rel="sponsored nofollow" attributes. No personal identifiers (PII), bank details, or KYC records are ever passed in referral URLs. Clear, prominent disclosures are rendered on all comparison surfaces.',
    consequences: [
      'Privacy by design (GDPR/CCPA compliant).',
      'Affiliate monetization supports free consumer tools without corrupting search integrity.',
      'Audit logging of outbound referral clicks with session-level attribution.'
    ],
    compliance: 'FTC Affiliate Disclosure Guidelines, Google Webmaster Sponsored Link Guidelines.'
  },
  {
    id: 'ADR-TRANSFER-006',
    title: 'Corridor SEO Eligibility Engine vs Anti-Spam Safeguards',
    status: 'ACCEPTED',
    context: 'Generating programmatic doorway pages for all 200x200 country combinations results in low-quality search engine spam and degraded user experiences on unsupported corridors.',
    decision: 'We implement a `TransferCorridorSeoEligibilityEngine`. A corridor URL is marked `INDEXABLE` only if: (1) active verified provider quotes exist, (2) comparison is functional, (3) search demand score > 30, and (4) unique corridor regulatory and payout content is published.',
    consequences: [
      'High-quality, authoritative search engine footprint.',
      'Zero indexation of broken, empty, or unsupported remittance routes.',
      'Automatic fallback to `NOINDEX` if provider coverage drops below threshold.'
    ],
    compliance: 'Google Search Essentials & Quality Rater Guidelines (E-E-A-T).'
  }
];

// ============================================================================
// 4. MASTER DELIVERABLES (A -> AU) COMPLETE SPECIFICATIONS
// ============================================================================

export const DELIVERABLES_P5: DeliverableP5[] = [
  {
    code: 'A',
    titleEn: 'Money Transfer Product Architecture',
    titleFr: 'Architecture Produit des Transferts d\'Argent',
    category: 'Product & Providers',
    summary: 'The YamooPay Global Money Transfer Intelligence Engine serves as an independent, trustworthy intelligence layer between consumers and global remittance providers.',
    details: [
      'Core focus: "How much will my recipient actually receive?" over isolated superficial fee banners.',
      'Complete coverage of two comparison modes: Mode A ("I want to send $X") and Mode B ("Recipient should receive €Y").',
      'Unified presentation of total transfer cost: Explicit Fee + FX Markup + Known Delivery Charges.',
      'Global-first design respecting Light Mode aesthetics and responsive touch-first layouts.'
    ],
    architectureNotes: 'Engine coordinates with Prompt 04 FX intelligence for real-time benchmark exchange rates.'
  },
  {
    code: 'B',
    titleEn: 'Provider Ecosystem & Capability Matrix',
    titleFr: 'Écosystème des Fournisseurs & Matrice de Capacités',
    category: 'Product & Providers',
    summary: 'Comprehensive registry of top global and regional providers with explicit integration modes and capability discovery.',
    details: [
      'Wise, Remitly, Western Union, MoneyGram, Ria, WorldRemit, Xoom, Paysend, OFX, Instarem, LemFi, TapTap Send.',
      'Zero-scraping policy: All quote data derived strictly through official APIs, partner feeds, or authorized manual audits.',
      'Provider status tracking: ACTIVE, PARTIAL, DEGRADED, TEMPORARILY_UNAVAILABLE, DISABLED, RETIRED.',
      'Transparent capability mapping: Quote API, Partner Feed, Affiliate Deep Link, Webhook, Sandbox.'
    ],
    architectureNotes: 'Providers maintain distinct capabilities per sending-receiving country combination.'
  },
  {
    code: 'C',
    titleEn: 'Global Corridor Architecture & 5 Expansion Waves',
    titleFr: 'Architecture des Corridors Mondiaux & 5 Vagues d\'Expansion',
    category: 'Corridors & Rails',
    summary: 'Multi-corridor mapping distinguishing country-to-country routes from raw currency pairs.',
    details: [
      'Wave 1 (US, CA), Wave 2 (UK, EU, CH), Wave 3 (APAC), Wave 4 (Middle East, LATAM), Wave 5 (Africa).',
      'Country ≠ Currency: US→India (USD→INR) is distinctly modeled with specific local regulatory limits from Singapore→India (SGD→INR).',
      'Corridor statuses: ACTIVE, PARTIAL, RESTRICTED, PLANNED, UNSUPPORTED.',
      'Corridor metadata: supported payout rails, average delivery speed, search volume, regulatory compliance notes.'
    ],
    architectureNotes: 'Corridors enforce sender KYC and recipient limit validation rules prior to quote fetching.'
  },
  {
    code: 'D',
    titleEn: 'Payment & Payout Rails Modeling',
    titleFr: 'Modélisation des Rails de Paiement & Réception',
    category: 'Corridors & Rails',
    summary: 'Granular separation between funding methods (pay-in) and disbursement methods (pay-out).',
    details: [
      'Pay-in: Bank Account/ACH, Debit Card, Credit Card, Wire Transfer, Open Banking/SEPA Instant, Apple/Google Pay, Cash in Store.',
      'Pay-out: Bank Deposit, Cash Pickup, Mobile Money (MTN, Orange, M-Pesa, Airtel, Wave), Mobile Wallet (GCash, bKash), Card Deposit, Home Delivery.',
      'Pay-in and pay-out method impact on pricing, speed, and fee deduction models.',
      'Clear distinction between international transfer providers and local disbursement networks.'
    ],
    architectureNotes: 'Quotes are indexed and filtered by specific pay-in/payout combinations to prevent mismatched comparisons.'
  },
  {
    code: 'E',
    titleEn: 'Canonical Quote Model & Precision Financial Math',
    titleFr: 'Modèle de Quote Canonique & Mathématiques Financières de Précision',
    category: 'Precision & Financial Math',
    summary: 'Standardized quote entity eliminating JavaScript floating-point drift and normalizing fee models.',
    details: [
      'High-precision financial arithmetic with integer scaling factor 10^8.',
      'Fee deduction models: Fee added on top (Total Debit = Send + Fee) vs Fee deducted from principal (Amount Converted = Send - Fee).',
      'Provider Effective Rate = Recipient Amount / Amount Converted.',
      'FX Markup % = ((YamooPay Benchmark Rate - Provider Rate) / Benchmark Rate) * 100.',
      'Estimated Total Cost = Explicit Fee + FX Markup Cost.'
    ],
    architectureNotes: 'Derived metrics marked explicitly with `derivedByYamooPay: true`.'
  },
  {
    code: 'F',
    titleEn: 'Ranking Architecture & Anti-Pay-to-Win Pledge',
    titleFr: 'Architecture de Classement & Engagement Anti-Pay-to-Win',
    category: 'Ranking & Governance',
    summary: 'Transparent, objective ranking engine with strict segregation between User Value and Commercial Affiliate revenue.',
    details: [
      'Default sort: Highest Recipient Amount (or transparent User Value Score combining speed, freshness, and total cost).',
      'Absolute prohibition of pay-to-win: `UserValueScore += affiliateCommission` is strictly banned.',
      'Clear user-facing trust labels: Verified quote, Current estimate, Promotional rate, Last checked.',
      'Sponsored placements (if any) isolated into a distinct container with prominent labeling.'
    ],
    architectureNotes: 'Audit logging verifies that organic ranks match pure mathematical formulas.'
  },
  {
    code: 'G',
    titleEn: 'Affiliate Engine & Monetization Disclosures',
    titleFr: 'Moteur d\'Affiliation & Divulgations de Monétisation',
    category: 'Ranking & Governance',
    summary: 'Compliant affiliate referral engine with privacy-preserving deep linking and legal disclosures.',
    details: [
      'FTC-compliant affiliate disclosure: "YamooPay may earn a commission when you use some provider links. This does not affect rates or ranking."',
      'Deep links pass country and currency parameters without exposing user PII.',
      'Rel="sponsored nofollow" attributes configured for search engine compliance.',
      'Session-level anonymous click and referral tracking.'
    ],
    architectureNotes: 'Zero dependency between affiliate availability and provider inclusion in comparator.'
  },
  {
    code: 'H',
    titleEn: 'Transfer Command Center & Operations Telemetry',
    titleFr: 'Centre de Commandement des Transferts & Télémétrie Opérationnelle',
    category: 'Architecture & ADRs',
    summary: 'Real-time operational cockpit for payments engineers and compliance teams.',
    details: [
      'Live KPIs: Active Providers, Active Corridors, Quote Success Rate, Fresh vs Expired Quotes, Latency, Anomaly Alerts.',
      'Quote Inspector: Raw source payload, normalized fields, benchmark rate, derived FX markup, ranking score.',
      'Quarantine triage for malformed quotes, impossible exchange rates, or stale timestamps.',
      'Affiliate performance vs User Value score integrity audit.'
    ],
    architectureNotes: 'Bitemporal quote snapshot retention for regulatory auditability and pricing trend analysis.'
  }
];
